import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils";
import { toast } from "sonner";
import {
  Save, RotateCcw, Loader2, CreditCard, QrCode, Trash2, Upload,
} from "lucide-react";

/**
 * iter178 — Admin-only Payment Details editor.
 *
 * One singleton `payment_settings` doc backing every client "Make a
 * Payment" section + the invoice footer. Fields:
 *   • Account Holder Name / Bank Name / Account Number / IFSC
 *   • UPI ID / UPI Display Name / Payment Instructions
 *   • UPI QR image (data-URL, ≤ 500 KB, admin can remove)
 *
 * Admin-only — the tab is exposed to admins only; the backend PUT
 * endpoint also refuses non-admin writes. See server.py for the
 * complete RBAC layer.
 */
export default function PaymentDetailsCard() {
  const [form, setForm] = useState(EMPTY);
  const [initial, setInitial] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [meta, setMeta] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get("/settings/payment");
        if (!alive) return;
        const clean = pick(data);
        setForm(clean);
        setInitial(clean);
        setMeta({ updated_at: data.updated_at });
      } catch (e) {
        setErr(formatApiError(e?.response?.data?.detail) || e?.message || "Load failed");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("QR image is too large — please upload an image under 500 KB");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, upi_qr_url: String(reader.result || "") }));
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const clearQr = () => setForm((f) => ({ ...f, upi_qr_url: "" }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.put("/settings/payment", form);
      const clean = pick(data);
      setForm(clean);
      setInitial(clean);
      setMeta({ updated_at: data.updated_at });
      toast.success("Payment details saved · appears on every client tracker");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setForm(initial); setErr(""); };

  if (loading) {
    return (
      <section className="card p-6" data-testid="settings-payment-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
        Loading payment details…
      </section>
    );
  }

  return (
    <section className="card p-6 space-y-6" data-testid="settings-payment-details">
      <div className="flex items-start gap-3">
        <CreditCard className="w-5 h-5 text-rose-700 mt-1" strokeWidth={1.6} />
        <div className="flex-1">
          <div className="editorial-eyebrow">Global · Client-facing</div>
          <h2 className="font-heading text-2xl font-semibold text-stone-900">
            Payment Details
          </h2>
          <p className="text-sm text-stone-500 mt-1">
            Configure once — these details appear on every client's
            "Make a Payment" section and in the invoice footer. Update
            anytime; existing invoices already generated stay unchanged.
          </p>
          {meta?.updated_at && (
            <p className="text-[11px] text-stone-400 mt-1">
              Last updated {new Date(meta.updated_at).toLocaleString("en-IN")}.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Field
          label="Account Holder Name" testid="pd-account-holder"
          value={form.account_holder_name} onChange={set("account_holder_name")}
          placeholder="Kahani by Rakesh Janardan"
        />
        <Field
          label="Bank Name" testid="pd-bank-name"
          value={form.bank_name} onChange={set("bank_name")}
          placeholder="HDFC Bank"
        />
        <Field
          label="Account Number" testid="pd-account-number"
          value={form.account_number} onChange={set("account_number")}
          placeholder="1234567890"
        />
        <Field
          label="IFSC Code" testid="pd-ifsc"
          value={form.ifsc_code} onChange={set("ifsc_code")}
          placeholder="HDFC0000123"
        />
        <Field
          label="UPI ID" testid="pd-upi-id"
          value={form.upi_id} onChange={set("upi_id")}
          placeholder="kahani@upi"
        />
        <Field
          label="UPI Display Name" testid="pd-upi-display"
          value={form.upi_display_name} onChange={set("upi_display_name")}
          placeholder="Kahani Studio"
        />
      </div>

      <div className="space-y-1.5">
        <label className="label">Payment Instructions</label>
        <textarea
          className="input min-h-[80px] resize-y"
          rows={3}
          value={form.payment_instructions}
          onChange={set("payment_instructions")}
          placeholder="Please mention your couple/project name while making the payment. After payment, please share the transaction screenshot with Kahani Studio."
          data-testid="pd-instructions"
        />
      </div>

      {/* UPI QR upload */}
      <div className="space-y-2">
        <label className="label flex items-center gap-1.5">
          <QrCode className="w-3.5 h-3.5" strokeWidth={1.8} /> UPI QR Code
        </label>
        <div className="flex items-start gap-4 flex-wrap">
          {form.upi_qr_url ? (
            <div className="relative w-40 h-40 rounded-lg border border-stone-200 overflow-hidden bg-white" data-testid="pd-qr-preview">
              <img src={form.upi_qr_url} alt="UPI QR preview" className="w-full h-full object-contain" />
            </div>
          ) : (
            <div className="w-40 h-40 rounded-lg border border-dashed border-stone-300 flex items-center justify-center text-stone-400 text-xs text-center px-3">
              No QR uploaded
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFile}
              data-testid="pd-qr-file"
            />
            <button type="button" className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()} data-testid="pd-qr-upload">
              <Upload className="w-3.5 h-3.5" /> {form.upi_qr_url ? "Replace QR" : "Upload QR"}
            </button>
            {form.upi_qr_url && (
              <button type="button" className="btn btn-outline btn-sm !text-red-700 !border-red-200 hover:!bg-red-50" onClick={clearQr} data-testid="pd-qr-remove">
                <Trash2 className="w-3.5 h-3.5" /> Remove QR
              </button>
            )}
            <p className="text-[11px] text-stone-400">PNG / JPG · Max 500 KB.</p>
          </div>
        </div>
      </div>

      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="pd-error">
          {err}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-stone-200">
        <button type="button" className="btn btn-outline" onClick={cancel} disabled={!dirty || saving} data-testid="pd-cancel">
          <RotateCcw className="w-4 h-4" /> Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={!dirty || saving} data-testid="pd-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save changes
        </button>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <label className="label">{label}</label>
      <input
        className="input"
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  );
}

const EMPTY = {
  account_holder_name: "",
  bank_name: "",
  account_number: "",
  ifsc_code: "",
  upi_id: "",
  upi_display_name: "",
  payment_instructions: "",
  upi_qr_url: "",
};

function pick(d) {
  return {
    account_holder_name: d.account_holder_name || "",
    bank_name: d.bank_name || "",
    account_number: d.account_number || "",
    ifsc_code: d.ifsc_code || "",
    upi_id: d.upi_id || "",
    upi_display_name: d.upi_display_name || "",
    payment_instructions: d.payment_instructions || "",
    upi_qr_url: d.upi_qr_url || "",
  };
}
